/**
 * Money is always stored and moved as an integer number of minor units
 * (cents for CAD, francs for XAF). Never use floats for balances.
 */

export const CURRENCIES = {
  CAD: { minor: 2, label: "Canadian dollar" },
  USD: { minor: 2, label: "US dollar" },
  EUR: { minor: 2, label: "Euro" },
  XAF: { minor: 0, label: "Central African CFA franc" },
  XOF: { minor: 0, label: "West African CFA franc" },
  NGN: { minor: 2, label: "Nigerian naira" },
  GHS: { minor: 2, label: "Ghanaian cedi" },
  KES: { minor: 2, label: "Kenyan shilling" },
} as const;

export type Currency = keyof typeof CURRENCIES;

export const FUNDING_METHODS = ["card", "interac", "mobile_money", "bank"] as const;
export type FundingMethod = (typeof FUNDING_METHODS)[number];
export const CURRENCY_CODES = Object.keys(CURRENCIES) as Currency[];

export function isCurrency(value: string): value is Currency {
  return value in CURRENCIES;
}

export function minorDigits(currency: Currency): number {
  return CURRENCIES[currency].minor;
}

/**
 * Parse a user-typed amount ("1,000.50", "1000", "12.5") into minor units.
 * Returns null when the text is not a valid positive amount for the currency.
 */
/**
 * Parse what a person typed into minor units, reading separators the way their language does:
 * "12,50" is twelve and a half in French; "1,250.75" and "1 250,75" are both 1 250.75.
 * Anything ambiguous or malformed returns null rather than a silently different amount.
 */
export function parseAmount(input: string, currency: Currency, locale = "en"): number | null {
  const normalized = normalizeAmountText(input, locale);
  if (normalized === null) return null;
  const digits = minorDigits(currency);
  const [whole = "0", frac = ""] = normalized.split(".");
  if (frac.length > digits) return null;
  const minor = Number(whole) * 10 ** digits + Number(frac.padEnd(digits, "0") || "0");
  if (!Number.isSafeInteger(minor) || minor <= 0) return null;
  return minor;
}

/** "1 250,75" / "1,250.75" / "12,50" → "1250.75" / "1250.75" / "12.50"; null when unclear. */
export function normalizeAmountText(input: string, locale = "en"): string | null {
  const s = input.trim().replace(/[\s\u00a0\u202f_']/g, "");
  if (!/^[\d.,]+$/.test(s) || !/\d/.test(s)) return null;
  const french = locale.toLowerCase().startsWith("fr");
  const commas = (s.match(/,/g) ?? []).length;
  const dots = (s.match(/\./g) ?? []).length;
  const grouped = (body: string, sep: string) => new RegExp(`^\\d{1,3}(\\${sep}\\d{3})+$`).test(body);
  let out: string;
  if (commas && dots) {
    // Both present: the last one is the decimal mark, the other must group by thousands.
    const dec = s.lastIndexOf(",") > s.lastIndexOf(".") ? "," : ".";
    const grp = dec === "," ? "." : ",";
    const i = s.lastIndexOf(dec);
    const body = s.slice(0, i);
    if ((dec === "," ? commas : dots) !== 1 || !grouped(body, grp)) return null;
    out = `${body.split(grp).join("")}.${s.slice(i + 1)}`;
  } else if (commas) {
    if (commas > 1) {
      if (!grouped(s, ",")) return null;
      out = s.replace(/,/g, "");
    } else if (french) out = s.replace(",", ".");
    // English: "1,250" groups thousands; "12,50" can only be a decimal comma.
    else out = grouped(s, ",") ? s.replace(",", "") : s.replace(",", ".");
  } else if (dots > 1) {
    if (!grouped(s, ".")) return null;
    out = s.replace(/\./g, "");
  } else out = s;
  return /^\d+(\.\d+)?$/.test(out) ? out : null;
}

/** Convert minor units to a major-unit decimal string ("1250" CAD -> "12.50"). */
export function toMajorString(minor: number, currency: Currency): string {
  const digits = minorDigits(currency);
  const sign = minor < 0 ? "-" : "";
  const abs = Math.abs(minor);
  if (digits === 0) return `${sign}${abs}`;
  const whole = Math.floor(abs / 10 ** digits);
  const frac = String(abs % 10 ** digits).padStart(digits, "0");
  return `${sign}${whole}.${frac}`;
}

export function formatMoney(minor: number, currency: Currency, locale = "en-CA"): string {
  const digits = minorDigits(currency);
  // CFA francs are written "25 000 FCFA" across Central and West Africa, not "XAF 25,000".
  if (currency === "XAF" || currency === "XOF") {
    const n = new Intl.NumberFormat(locale.startsWith("fr") ? "fr-FR" : locale, { maximumFractionDigits: 0 }).format(minor);
    return `${n} FCFA`;
  }
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(minor / 10 ** digits);
}
